import { ReactElement, useState } from 'react'
import { ChannelCell } from 'app/components/contacts/ChannelCell'
import { ContactChannelMappingForm } from 'app/components/contacts/ContactChannelMappingForm'
import { InlineAddActionLink } from 'app/components/shared/InlineAddActionLink'
import Spinner from 'app/components/Spinner'
import type { ContactJson } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { notifySuccess } from 'app/services/notify.client'
import { useChannelMappingStore } from 'app/stores/StoreContext'
import { useUser } from 'app/utils'
import { ChannelMappingType } from 'lib/channelDefaults'
import { isSMCOrg } from 'lib/isSMCOrg'
import { debug, logError } from 'lib/logging'

export function ChannelMappingCell({ contact }: { contact?: ContactJson }): ReactElement {
  const user = useUser()
  if (!user) return <></>
  const isSMC = isSMCOrg(user.orgId)
  const channelMappingStore = useChannelMappingStore()
  const [channel, setChannel] = useState<string | undefined>(contact?.channel)
  const [saving, setSaving] = useState(false)

  const { utmSource, utmMedium } = contact || {}
  const openModal = ({ utmSource, utmMedium }: { utmSource?: string; utmMedium?: string }) => {
    showModal(
      saving ? (
        <Spinner className="absolute inset-0 m-auto" />
      ) : (
        <ContactChannelMappingForm
          channelMapping={
            {
              orgId: user.orgId,
              workspace: user.workspaceId,
              source: utmSource,
              medium: utmMedium,
            } as ChannelMappingType
          }
          handleSubmit={async (channelMapping) => {
            if (!channelMapping.source || !channelMapping.medium || !channelMapping.channel)
              return { error: `Source, medium and channel are required` }
            setSaving(true)
            try {
              const result = await channelMappingStore.create({
                source: channelMapping.source,
                medium: channelMapping.medium,
                channel: channelMapping.channel,
              })
              if (result.error) return { error: result.error.message }
              setChannel(channelMapping.channel)
              notifySuccess(`Change Saved. This may take some time to fully apply.`)
              closeModal()
            } catch (err: unknown) {
              const msg = err instanceof Error ? err.message : `Failed to save channel mapping`
              logError(`Failed to save channel mapping`, err)
              return { error: msg }
            } finally {
              setSaving(false)
            }
          }}
        />
      ),
      {
        title: `Add Channel Mapping`,
        isFullScreen: false,
        removePadding: true,
        useRadix: true,
      },
    )
  }

  if (isSMC) return <ChannelCell channel={channel} />
  return utmSource && utmMedium ? (
    channel ? (
      <ChannelCell channel={channel} />
    ) : (
      <InlineAddActionLink
        label="+ Add Channel Mapping"
        onClick={() => {
          debug(
            [`contacts`, `channel mapping`],
            `create a new channel for contact`,
            contact?.id,
            channel,
            utmSource,
            utmMedium,
          )
          openModal({
            utmSource,
            utmMedium,
          })
        }}
      />
    )
  ) : (
    <></>
  )
}
